/**
 * Products in use per hardware type, with hardware counts — for the hardware
 * list's quick-access toggles.
 *
 * The quick toggles historically filtered by hardware TYPE only. For systems
 * where one type spans several meaningful products (e.g. a bike system running
 * Pedal and Recumbent bikes, or a lawn-games system with one product per game),
 * the toggles are more useful per PRODUCT. This hook answers, in one place:
 * which products are actually in use in the scope, under which type, and how
 * many units each has.
 *
 * Mirrors useHardwareTypeCounts' approach (no grouped aggregate in the GraphQL
 * API yet): one request lists the products for the scope's types, a second
 * request asks one aliased `listHardware(...) { count }` per product. Both stay
 * a single round trip each; product lists per type are short.
 */

import { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { getGraphQLClient } from '../../../../api/graphql';

export interface HardwareProductInUse {
  /** GraphQL product id (used by the hardware filter). */
  id: string;
  name: string;
  /** Lowercase hardware type name the product belongs to. */
  typeName: string;
  count: number;
}

interface Params {
  /** Type names in use in the scope, e.g. ['bike', 'lock']. */
  typeNames: string[];
  /** System-scoped count (system view). Takes precedence over organizationId. */
  systemId?: string;
  /** Organization-scoped count (organization view). */
  organizationId?: string;
  enabled?: boolean;
}

const PRODUCTS_QUERY = `query HardwareProductsInUse($filter: HardwareProductFilterInput) {
  listHardwareProducts(filter: $filter, first: 200, sort: [{ field: NAME, order: ASC }]) {
    edges { node { id name hardwareType { name } } }
  }
}`;

export const buildProductCountsQuery = (productCount: number): string => {
  const params = Array.from(
    { length: productCount },
    (_, index) => `$f${index}: HardwareFilterInput`
  ).join(', ');
  const fields = Array.from(
    { length: productCount },
    (_, index) => `  p${index}: listHardware(filter: $f${index}, first: 1) { count }`
  ).join('\n');
  return `query HardwareProductCounts(${params}) {\n${fields}\n}`;
};

export function useHardwareProductQuickFilters({
  typeNames,
  systemId,
  organizationId,
  enabled = true,
}: Params) {
  const names = useMemo(() => [...typeNames].sort(), [typeNames]);

  const scopeFilter = useMemo(() => {
    if (systemId) return { systemId: { eq: systemId } };
    if (organizationId) return { organizationId: { eq: organizationId } };
    return null;
  }, [systemId, organizationId]);

  const { data, isLoading, error } = useQuery({
    queryKey: [
      'hardwareProductQuickFilters',
      systemId ?? null,
      organizationId ?? null,
      names,
    ],
    enabled: enabled && Boolean(scopeFilter) && names.length > 0,
    // The chip row waits for this data before rendering; keep revisits instant.
    staleTime: 5 * 60 * 1000,
    queryFn: async (): Promise<HardwareProductInUse[]> => {
      if (names.length === 0) return [];
      const client = getGraphQLClient();

      const productsResponse = await client.request<{
        listHardwareProducts: {
          edges: Array<{
            node: {
              id: string;
              name: string | null;
              hardwareType: { name: string | null } | null;
            } | null;
          } | null> | null;
        } | null;
      }>(PRODUCTS_QUERY, {
        filter: { hardwareType: { name: { in: names } } },
      });

      const products = (productsResponse?.listHardwareProducts?.edges ?? [])
        .map((edge) => edge?.node)
        .filter(
          (node): node is { id: string; name: string; hardwareType: { name: string } } =>
            Boolean(node?.id && node?.name && node?.hardwareType?.name)
        );
      if (products.length === 0) return [];

      const variables = Object.fromEntries(
        products.map((product, index) => [
          `f${index}`,
          { ...scopeFilter, hardwareProductId: { eq: product.id } },
        ])
      );
      const countsResponse = await client.request<
        Record<string, { count: number | null } | null>
      >(buildProductCountsQuery(products.length), variables);

      return products
        .map((product, index) => ({
          id: product.id,
          name: product.name,
          typeName: product.hardwareType.name.toLowerCase(),
          count: countsResponse?.[`p${index}`]?.count ?? 0,
        }))
        .filter((item) => item.count > 0);
    },
  });

  return {
    data: (data as HardwareProductInUse[] | undefined) ?? [],
    isLoading,
    error,
  };
}
